import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/do-not-sleep-amnesia-ibiza-4-june-2026";
const IMAGE_URL = "https://www.hey-lets-travel.com/assets/30fb2c7a-8abc-4a3c-b91a-8ebe388ee80e.png";
const TICKET_URL = "https://www.ibiza-spotlight.com/night/promoters/do-not-sleep-amnesia";
const CONCIERGE_URL = "/concierge";
const EVENTS_URL = "/events";

const LINEUP = [
  "Sidney Charles B2B L.P. Rhythm",
  "Ozzie Guven",
  "jWave B2B Boss Priester",
  "Lissy Lubeck",
];

const KEYWORDS = [
  "Do Not Sleep Ibiza 2026",
  "Do Not Sleep Amnesia tickets",
  "Do Not Sleep Amnesia Ibiza 4 June",
  "Sidney Charles Ibiza 2026",
  "L.P. Rhythm Ibiza",
  "Ozzie Guven Ibiza",
  "jWave Boss Priester Ibiza",
  "Lissy Lubeck Ibiza",
  "Amnesia Ibiza Thursday 2026",
  "Ibiza house music events June 2026",
  "Ibiza tech house events 2026",
  "Ibiza minimal house events",
  "Amnesia Ibiza tickets",
  "HLT Ibiza concierge",
  "Ibiza hotel and ticket package",
  "Ibiza party holiday 2026",
];

const FAQS = [
  {
    question: "When is Do Not Sleep at Amnesia Ibiza?",
    answer: "This Do Not Sleep event takes place at Amnesia Ibiza on Thursday 4 June 2026, running from 23:00 to 06:00.",
  },
  {
    question: "Who is playing at Do Not Sleep Amnesia on 4 June?",
    answer: "The listed lineup includes Sidney Charles B2B L.P. Rhythm, Ozzie Guven, jWave B2B Boss Priester and Lissy Lubeck.",
  },
  {
    question: "Where is Do Not Sleep taking place?",
    answer: "The event takes place at Amnesia Ibiza, one of the island's most recognised clubs for house, tech house and late-night dancefloor culture.",
  },
  {
    question: "What music should I expect?",
    answer: "Expect house, tech house, minimal, rolling groove and underground Ibiza club energy.",
  },
  {
    question: "Can HLT help with hotels, tables and concierge for this event?",
    answer: "Yes. HLT can help with hotels, villas, transfers, VIP table enquiries, after-party planning and full concierge packages around Amnesia Ibiza events.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Do Not Sleep at Amnesia Ibiza 2026: Sidney Charles, L.P. Rhythm, Ozzie Guven and More",
  description:
    "Do Not Sleep returns to Amnesia Ibiza on Thursday 4 June 2026 with Sidney Charles B2B L.P. Rhythm, Ozzie Guven, jWave B2B Boss Priester and Lissy Lubeck.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-06-03",
  dateModified: "2026-06-03",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const eventSchema = {
  "@context": "https://schema.org",
  "@type": "MusicEvent",
  name: "Do Not Sleep at Amnesia Ibiza",
  startDate: "2026-06-04T23:00:00+02:00",
  endDate: "2026-06-05T06:00:00+02:00",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  eventStatus: "https://schema.org/EventScheduled",
  image: [IMAGE_URL],
  location: {
    "@type": "Place",
    name: "Amnesia Ibiza",
    address: "Ibiza, Spain",
  },
  performer: LINEUP.map((artist) => ({ "@type": "MusicGroup", name: artist })),
  offers: {
    "@type": "Offer",
    url: TICKET_URL,
    priceCurrency: "EUR",
    availability: "https://schema.org/InStock",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "View Do Not Sleep Tickets" }) {
  return (
    <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Plan Amnesia with HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function DoNotSleepAmnesiaJuneArticle() {
  return (
    <>
      <Helmet>
        <title>Do Not Sleep Amnesia Ibiza 2026 Tickets | 4 June HLT Guide</title>
        <meta
          name="description"
          content="Book Do Not Sleep at Amnesia Ibiza on Thursday 4 June 2026. Lineup includes Sidney Charles B2B L.P. Rhythm, Ozzie Guven, jWave B2B Boss Priester and Lissy Lubeck. Plan Ibiza tickets, hotels and concierge with HLT."
        />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Do Not Sleep Amnesia Ibiza 2026 Tickets | 4 June HLT Guide" />
        <meta property="og:description" content="Plan Do Not Sleep at Amnesia Ibiza with HLT tickets, hotels, VIP support and concierge planning." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Do Not Sleep Amnesia Ibiza 2026 Tickets" />
        <meta name="twitter:description" content="Do Not Sleep returns to Amnesia Ibiza on 4 June 2026 with Sidney Charles B2B L.P. Rhythm, Ozzie Guven, jWave B2B Boss Priester and Lissy Lubeck." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">Ibiza 2026 guide</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              Do Not Sleep at Amnesia Ibiza 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              Do Not Sleep returns to Amnesia Ibiza on Thursday 4 June 2026 with Sidney Charles B2B L.P. Rhythm, Ozzie Guven, jWave B2B Boss Priester and Lissy Lubeck.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta />
              <ConciergeCta children="Build the Ibiza night with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>Do Not Sleep</dd></div>
              <div><dt className="text-white">Venue</dt><dd>Amnesia Ibiza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Thursday 4 June 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>23:00 - 06:00</dd></div>
              <div><dt className="text-white">Sound</dt><dd>House, tech house, minimal and groove-led Ibiza club music</dd></div>
              <div><dt className="text-white">Travel angle</dt><dd>Tickets, hotels, villas, VIP extras, transfers and concierge planning</dd></div>
            </dl>
          </aside>
        </header>

        <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="group block overflow-hidden border border-brand-500/25 bg-black shadow-[0_24px_90px_rgba(0,0,0,0.55)] transition hover:border-brand-500/70">
          <img src="/assets/30fb2c7a-8abc-4a3c-b91a-8ebe388ee80e.png" alt="Do Not Sleep at Amnesia Ibiza event poster" className="block h-auto w-full transition duration-500 group-hover:scale-[1.01]" />
        </a>

        <div className="mx-auto max-w-4xl space-y-10">
          <section className="space-y-4 text-base leading-8 text-slate-300">
            <p>
              Do Not Sleep at Amnesia Ibiza is one of those events that tells you exactly what kind of Ibiza night you are stepping into: late, loud, underground, music-led and built for people who still care about the dancefloor.
            </p>
            <p>
              The Thursday 4 June edition gives HLT travellers a clean early-season Amnesia date to build around. It runs from 23:00 to 06:00, giving groups a proper full-night anchor for hotels, transfers, VIP extras, after parties and the rest of the Ibiza plan.
            </p>
            <p>
              This is not a random club listing. It is a house and tech house event at one of the island's most important venues, with a lineup that speaks directly to the underground crowd.
            </p>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View Do Not Sleep ticket options" />
              <ConciergeCta children="Ask HLT to build the full Ibiza plan" />
            </div>
          </section>

          <ArticleSection title="Why Do Not Sleep is a proper Ibiza night">
            <p>
              Do Not Sleep has always sat in the more serious side of Ibiza nightlife. The brand is known for house, tech house, minimal pressure, stripped-back energy and lineups that work for people who want the night to build properly.
            </p>
            <p>
              That matters because Ibiza can be overwhelming. There are hundreds of events across the season, but only some of them give you a clear sound and a clear reason to build the trip around them. Do Not Sleep is one of those nights.
            </p>
            <p>
              At Amnesia, the concept has the room it needs. The venue gives the party scale, history and the kind of late-night intensity that suits rolling drums, deep basslines and long sets.
            </p>
          </ArticleSection>

          <ArticleSection title="The lineup: Sidney Charles B2B L.P. Rhythm, Ozzie Guven and more">
            <p>
              The 4 June lineup is built around groove and club pressure. Sidney Charles B2B L.P. Rhythm is the headline pairing, connecting Sidney Charles' respected house and tech house profile with L.P. Rhythm's sharp, modern, dancefloor-focused sound.
            </p>
            <p>
              Ozzie Guven brings a strong UK underground angle, with records and DJ sets that fit the modern minimal and tech house lane. jWave B2B Boss Priester adds a deeper rolling energy, while Lissy Lubeck completes the bill with a fresh selector presence.
            </p>
            <p>
              Together, the lineup makes sense for Amnesia: it is not built around one pop moment, it is built around momentum from start to finish.
            </p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              {LINEUP.map((artist) => <li key={artist}>{artist}</li>)}
            </ul>
          </ArticleSection>

          <ArticleSection title="Why Amnesia is the right venue">
            <p>
              Amnesia is one of Ibiza's key clubbing institutions. For a brand like Do Not Sleep, the venue gives the night a sense of weight without taking the focus away from the music.
            </p>
            <p>
              A 23:00 to 06:00 slot is exactly what many Ibiza travellers want from Amnesia: pre-drinks, arrival, a full late-night session, then the choice of recovery, afters or a slower next day.
            </p>
            <p>
              That structure is useful for groups. You can plan the hotel, transfers, tables, restaurants and after-party options around one strong centrepiece instead of letting the trip become messy.
            </p>
          </ArticleSection>

          <ArticleSection title="Build your Ibiza trip around Do Not Sleep">
            <p>
              HLT is built for people who travel for music. Some guests only need the ticket. Others want the whole thing handled: hotel, villa, transport, VIP access, dinner, beach clubs, after parties and a clean group itinerary.
            </p>
            <p>For Do Not Sleep at Amnesia, HLT can help you plan:</p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              <li>Do Not Sleep ticket access</li>
              <li>Ibiza hotels near Ibiza Town, Talamanca, Playa d'en Bossa or San Antonio</li>
              <li>Group villas for private pre-parties, pool days and recovery time</li>
              <li>Airport transfers and Amnesia club transport</li>
              <li>VIP table enquiries and premium nightlife support</li>
              <li>After-party, villa party and private group planning</li>
              <li>Full Ibiza packages combining tickets, accommodation, transfers and concierge help</li>
            </ul>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View ticket options" />
              <ConciergeCta children="Request an Ibiza concierge package" />
              <Link to={EVENTS_URL} className="btn-secondary">Browse more Ibiza events</Link>
            </div>
          </ArticleSection>

          <ArticleSection title="Where to stay for Do Not Sleep at Amnesia">
            <p>
              Amnesia sits close to San Rafael, which means you have several strong accommodation routes depending on your group. Ibiza Town and Talamanca work well if you want restaurants, taxis, marina energy and a more polished base.
            </p>
            <p>
              Playa d'en Bossa makes sense if you are combining Do Not Sleep with Hi Ibiza, Ushuaia, beach clubs and a bigger nightlife schedule. San Antonio can work for value-led groups, but transport needs to be planned properly.
            </p>
            <p>
              For bigger groups, villas are often the strongest option. They give you privacy, pool time, space to reset and the ability to turn the whole trip into one shared base instead of splitting across hotel rooms.
            </p>
            <ConciergeCta children="Ask HLT where to stay for Amnesia" />
          </ArticleSection>

          <ArticleSection title="Who should go?">
            <p>
              This event is for people who like house, tech house, minimal, rolling groove and Ibiza nights that feel music-first. If your group wants a proper Amnesia session instead of a generic night out, Do Not Sleep belongs on the list.
            </p>
            <p>
              It also works well for travellers arriving early in June who want one strong club night to shape the rest of the trip. Book the ticket first, then build the hotel and concierge plan around it.
            </p>
          </ArticleSection>

          <ArticleSection title="SEO travel angle: why this date matters">
            <p>
              Searches for Ibiza house music events, Amnesia Ibiza tickets, Do Not Sleep Ibiza, Ibiza tech house events and Ibiza hotel and ticket packages all point to the same kind of traveller: someone who wants the event first, then the trip around it.
            </p>
            <p>
              That is the HLT lane. We help people find the right event, secure the route in, and make the wider travel experience feel easy rather than scattered.
            </p>
          </ArticleSection>

          <ArticleSection title="Final word">
            <p>
              Do Not Sleep at Amnesia Ibiza on Thursday 4 June 2026 is a clean early-season house music target: strong venue, focused lineup, late-night energy and a clear reason to build the trip properly.
            </p>
            <p>
              Book the ticket, sort the hotel and let HLT help you turn the night into a full Ibiza plan.
            </p>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View Do Not Sleep tickets" />
              <ConciergeCta children="Build the trip with HLT" />
            </div>
          </ArticleSection>

          <section className="border-t border-white/10 pt-8">
            <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
            <div className="mt-5 grid gap-3">
              {FAQS.map((item) => (
                <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                  <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                  <p className="mt-3 leading-7 text-slate-300">{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
